import { useState } from "react";
import { Phone, Mail, Globe } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";
import SocialLinks from "@/components/SocialLinks";

export default function Contact() {
  const { tr } = useLang();
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [contactSent, setContactSent] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState("");

  const submitContact = async (e) => {
    e.preventDefault();
    setContactBusy(true);
    setContactError("");
    try {
      await api.post("/contact", contactForm);
      setContactSent(true);
    } catch (err) {
      setContactError(formatApiError(err));
    }
    setContactBusy(false);
  };

  const inputCls =
    "w-full rounded-xl border border-aqua/20 bg-abyss/70 px-4 py-3 text-sm text-foam placeholder:text-foam/35 outline-none transition-all duration-300 focus:border-aqua focus:shadow-[0_0_20px_rgba(56,189,248,0.18)]";

  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="contact-page">
      <div className="relative overflow-hidden py-14">
        <div className="absolute inset-0 caustic-glow" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <SectionLabel>{tr("Contact")}</SectionLabel>
              <h1 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight text-foam">
                {tr("Parlons de votre parcours.")}
              </h1>
              <p className="mt-6 max-w-xl text-sm md:text-base leading-relaxed text-foam/65">
                {tr("Une question sur les modules, les dates ou le financement ? Stéphanie vous répond personnellement.")}
              </p>
              <div className="glass-card mt-9 max-w-xl rounded-2xl p-7" data-testid="contact-card">
                <p className="font-serif text-2xl text-foam">Stéphanie Vautey</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] text-aqua">
                  {tr("Directrice & Formatrice")}
                </p>
                <p className="mt-1 text-sm text-foam/55">OJANZU | École Française de Janzu</p>
                <div className="mt-6 space-y-3 text-sm">
                  <p className="flex flex-wrap gap-x-6 gap-y-2">
                    <a href="tel:+33964417003" data-testid="contact-phone-1" className="inline-flex items-center gap-2.5 text-foam/80 transition-colors hover:text-aqua">
                      <Phone size={15} className="text-aqua" /> 09 64 41 70 03
                    </a>
                    <a href="tel:+33782849817" data-testid="contact-phone-2" className="inline-flex items-center gap-2.5 text-foam/80 transition-colors hover:text-aqua">
                      <Phone size={15} className="text-aqua" /> 07 82 84 98 17
                    </a>
                  </p>
                  <a href="mailto:stephanie@janzu.fr" data-testid="contact-email" className="flex items-center gap-2.5 text-foam/80 transition-colors hover:text-aqua">
                    <Mail size={15} className="text-aqua" /> stephanie@janzu.fr
                  </a>
                  <a href="https://www.janzu.fr" target="_blank" rel="noreferrer" data-testid="contact-site" className="flex items-center gap-2.5 text-foam/80 transition-colors hover:text-aqua">
                    <Globe size={15} className="text-aqua" /> www.janzu.fr
                  </a>
                </div>
                <SocialLinks className="mt-7" />
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <form
                onSubmit={submitContact}
                data-testid="contact-form"
                className="glass-card rounded-2xl p-7"
              >
                <p className="font-serif text-2xl text-foam">{tr("Écrivez-nous")}</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <input
                    required
                    placeholder={tr("Votre nom")}
                    data-testid="contact-name-input"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className={inputCls}
                  />
                  <input
                    required
                    type="email"
                    placeholder={tr("Votre email")}
                    data-testid="contact-email-input"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <input
                  placeholder={tr("Téléphone (optionnel)")}
                  data-testid="contact-phone-input"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className={`${inputCls} mt-3`}
                />
                <textarea
                  required
                  minLength={10}
                  rows={5}
                  placeholder={tr("Votre message — modules, dates, financement…")}
                  data-testid="contact-message-input"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className={`${inputCls} mt-3 resize-none`}
                />
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                  value={contactForm.website}
                  onChange={(e) => setContactForm({ ...contactForm, website: e.target.value })}
                />
                {contactError && (
                  <p className="mt-3 rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" data-testid="contact-error">
                    {contactError}
                  </p>
                )}
                {contactSent ? (
                  <div className="mt-5 rounded-xl border border-aqua/40 bg-aqua/10 p-4 text-sm leading-relaxed text-foam" data-testid="contact-success">
                    {tr("Message envoyé ! Stéphanie vous répondra très vite — un accusé de réception arrive dans votre boîte mail.")}
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={contactBusy}
                    data-testid="contact-submit"
                    className="mt-5 w-full rounded-full bg-aqua py-4 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam hover:shadow-[0_0_36px_rgba(56,189,248,0.5)] disabled:opacity-50"
                  >
                    {contactBusy ? tr("Envoi…") : tr("Envoyer ma demande")}
                  </button>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
